/**
 * useDomainBundles — groups folder emails by primaryDomain and clusters
 * emails within each domain into topic groups (by threadId, then by
 * normalised subject).
 */
import { useMemo } from 'react';
import { DOMAIN_SUMMARIES, DOMAIN_LABELS } from '../mock/mockEmails';

/** Strip Re:/Fwd:/FW: prefixes and lowercase for grouping */
function normaliseSubject(s) {
  return (s || '')
    .replace(/^(re|fwd|fw)\s*:\s*/gi, '')
    .trim()
    .toLowerCase();
}

/**
 * Build topic groups from a list of emails belonging to the same domain.
 *
 * 1. Group by threadId (shared threads → one group)
 * 2. Remaining singletons grouped by normalised subject
 * 3. Sort groups by highest urgency desc, then most recent email
 */
function buildTopicGroups(emails) {
  // Step 1 — cluster by threadId
  const threadMap = {};
  emails.forEach((e) => {
    const key = e.threadId || e.id;
    if (!threadMap[key]) threadMap[key] = [];
    threadMap[key].push(e);
  });

  // Step 2 — merge single-email threads that share a normalised subject
  const subjectMap = {};
  const multiThreadGroups = [];

  Object.values(threadMap).forEach((group) => {
    if (group.length > 1) {
      multiThreadGroups.push(group);
      return;
    }
    const norm = normaliseSubject(group[0].subject);
    if (!subjectMap[norm]) subjectMap[norm] = [];
    subjectMap[norm].push(group[0]);
  });

  const allGroups = [
    ...multiThreadGroups,
    ...Object.values(subjectMap),
  ];

  // Step 3 — compute group metadata & sort
  const enriched = allGroups.map((groupEmails) => {
    const sorted = [...groupEmails].sort(
      (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt),
    );
    const highestUrgency = Math.max(...sorted.map((e) => e.urgency));
    const unreadCount = sorted.filter((e) => !e.isRead).length;
    const latest = sorted[0];

    return {
      label: latest.subject.replace(/^(RE|FWD|Fwd|Fw|Re):\s*/g, ''),
      emails: sorted,
      totalCount: sorted.length,
      unreadCount,
      highestUrgency,
      latestReceivedAt: latest.receivedAt,
      threadSummary: latest.threadSummary,
    };
  });

  // Sort: highest urgency first, then most recent
  enriched.sort((a, b) => {
    if (b.highestUrgency !== a.highestUrgency) return b.highestUrgency - a.highestUrgency;
    return new Date(b.latestReceivedAt) - new Date(a.latestReceivedAt);
  });

  return enriched;
}

/**
 * @param {Array} folderEmails — already filtered by activeFolder
 * @returns {Array} domainBundles sorted by urgency/recency
 */
export function useDomainBundles(folderEmails) {
  return useMemo(() => {
    // Group by primaryDomain
    const domainMap = {};
    folderEmails.forEach((e) => {
      const d = e.primaryDomain;
      if (!domainMap[d]) domainMap[d] = [];
      domainMap[d].push(e);
    });

    const bundles = Object.entries(domainMap).map(([domainKey, emails]) => {
      const totalCount = emails.length;
      const unreadCount = emails.filter((e) => !e.isRead).length;
      const highestUrgency = Math.max(...emails.map((e) => e.urgency));
      const sorted = [...emails].sort(
        (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt),
      );
      const latestReceivedAt = sorted[0].receivedAt;
      const topicGroups = buildTopicGroups(emails);

      return {
        domainKey,
        domainLabel: DOMAIN_LABELS[domainKey] || domainKey,
        totalCount,
        unreadCount,
        highestUrgency,
        latestReceivedAt,
        summary: DOMAIN_SUMMARIES[domainKey] || '',
        topicGroups,
      };
    });

    // Sort bundles: highest urgency first, then by unread count, then recency
    bundles.sort((a, b) => {
      if (b.highestUrgency !== a.highestUrgency) return b.highestUrgency - a.highestUrgency;
      if (b.unreadCount !== a.unreadCount) return b.unreadCount - a.unreadCount;
      return new Date(b.latestReceivedAt) - new Date(a.latestReceivedAt);
    });

    return bundles;
  }, [folderEmails]);
}
